import React, { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Upload, Trash2, ImageIcon, Loader2 } from "lucide-react";

interface Photo {
  id: string;
  file_path: string;
  caption: string | null;
  order_index: number;
}

interface Props {
  kitEventId: string;
  region: string;
}

const BUCKET = "conference-kit-photos";

// Compress image client-side to reduce storage usage
async function compressImage(file: File, maxWidth = 1600, quality = 0.78): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => {
      img.onload = () => {
        const ratio = Math.min(1, maxWidth / img.width);
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d")!;
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error("compression failed"))),
          "image/jpeg",
          quality,
        );
      };
      img.onerror = reject;
      img.src = e.target?.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export const PhotoUploader: React.FC<Props> = ({ kitEventId, region }) => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [cap, setCap] = useState(8);

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: settings }, { data, error }] = await Promise.all([
      supabase.from("app_settings" as any).select("value").eq("key", "conference_kit").maybeSingle(),
      (supabase as any)
        .from("kit_magazine_photos")
        .select("*")
        .eq("kit_event_id", kitEventId)
        .order("order_index"),
    ]);
    const settingValue = (settings as any)?.value;
    if (settingValue?.photos_per_region_cap) setCap(Number(settingValue.photos_per_region_cap));
    if (error) toast({ title: "Failed to load photos", description: error.message, variant: "destructive" });
    setPhotos((data as any) || []);
    setLoading(false);
  }, [kitEventId]);

  useEffect(() => { load(); }, [load]);

  const publicUrl = (path: string) =>
    supabase.storage.from(BUCKET).getPublicUrl(path).data.publicUrl;

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (photos.length + files.length > cap) {
      toast({
        title: `Limit is ${cap} photos per conference`,
        description: `You currently have ${photos.length}. Delete some before uploading more.`,
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }
    setUploading(true);
    let added = 0;
    for (const file of files) {
      try {
        const compressed = await compressImage(file);
        const safeRegion = region.toLowerCase().replace(/[^a-z0-9]/g, "-");
        const path = `${safeRegion}/${kitEventId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.jpg`;
        const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, compressed, {
          contentType: "image/jpeg",
          upsert: false,
        });
        if (upErr) throw upErr;
        const { error: insErr } = await (supabase as any).from("kit_magazine_photos").insert({
          kit_event_id: kitEventId,
          file_path: path,
          caption: file.name.replace(/\.[^.]+$/, ""),
          order_index: photos.length + added,
        });
        if (insErr) throw insErr;
        added++;
      } catch (err: any) {
        toast({ title: "Upload failed", description: err.message, variant: "destructive" });
        break;
      }
    }
    setUploading(false);
    e.target.value = "";
    if (added > 0) {
      toast({ title: `Uploaded ${added} photo${added === 1 ? "" : "s"}` });
      load();
    }
  };

  const updateCaption = async (id: string, caption: string) => {
    await (supabase as any).from("kit_magazine_photos").update({ caption }).eq("id", id);
  };

  const remove = async (p: Photo) => {
    if (!confirm("Delete this photo?")) return;
    await supabase.storage.from(BUCKET).remove([p.file_path]);
    await (supabase as any).from("kit_magazine_photos").delete().eq("id", p.id);
    toast({ title: "Photo deleted" });
    load();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between flex-wrap gap-2">
          <span className="flex items-center gap-2">
            <ImageIcon className="w-4 h-4" /> KIT Magazine Photos
            <Badge variant="secondary">{photos.length} / {cap}</Badge>
          </span>
          <div>
            <input
              id="kit-photo-input"
              type="file"
              accept="image/*"
              multiple
              hidden
              disabled={uploading || photos.length >= cap}
              onChange={handleUpload}
            />
            <Button
              size="sm"
              disabled={uploading || photos.length >= cap}
              onClick={() => document.getElementById("kit-photo-input")?.click()}
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              Upload Photos
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Loader2 className="animate-spin" />
        ) : photos.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            No photos yet. Add up to {cap} highlight photos for the KIT magazine and email attachments.
          </p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {photos.map((p) => (
              <div key={p.id} className="border rounded-md overflow-hidden bg-card">
                <div className="aspect-square bg-muted">
                  <img
                    src={publicUrl(p.file_path)}
                    alt={p.caption || "KIT photo"}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-2 space-y-1">
                  <Input
                    defaultValue={p.caption || ""}
                    placeholder="Caption…"
                    className="h-8 text-xs"
                    onBlur={(e) => updateCaption(p.id, e.target.value)}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    className="w-full h-7 text-red-600"
                    onClick={() => remove(p)}
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-muted-foreground mt-3">
          Photos are auto-compressed (~1600px, JPEG). Limit is enforced server-side at {cap} per conference to keep storage well within Supabase limits.
        </p>
      </CardContent>
    </Card>
  );
};
